export interface FileUploadConfig {
  acceptedTypes: string[];
  maxSize: number; // in bytes
  multiple: boolean;
  dropZone: boolean;
}

export interface UploadedFile {
  file: File;
  id: string;
  progress: number;
  status: FileUploadStatus;
  error?: string;
}

export enum FileUploadStatus {
  Pending = 'pending',
  Uploading = 'uploading',
  Success = 'success',
  Error = 'error',
}

export interface FileUploadEvent {
  files: UploadedFile[];
  type: 'select' | 'remove' | 'progress' | 'complete' | 'error';
}

export interface FileValidationResult {
  isValid: boolean;
  errors: string[];
}

// Predefined configurations for common use cases
export const NOTEBOOK_UPLOAD_CONFIG: FileUploadConfig = {
  acceptedTypes: ['.ipynb'],
  maxSize: 25 * 1024 * 1024, // 25MB
  multiple: false,
  dropZone: true,
};

export const IMAGE_UPLOAD_CONFIG: FileUploadConfig = {
  acceptedTypes: ['image/*'],
  maxSize: 10 * 1024 * 1024, // 10MB
  multiple: true,
  dropZone: true,
};

export const DOCUMENT_UPLOAD_CONFIG: FileUploadConfig = {
  acceptedTypes: ['.pdf', '.doc', '.docx', '.txt', '.rtf'],
  maxSize: 25 * 1024 * 1024, // 25MB
  multiple: true,
  dropZone: true,
};

export const SPREADSHEET_UPLOAD_CONFIG: FileUploadConfig = {
  acceptedTypes: ['.csv', '.xlsx', '.xls'],
  maxSize: 20 * 1024 * 1024, // 20MB
  multiple: false,
  dropZone: true,
};

export const MEDIA_UPLOAD_CONFIG: FileUploadConfig = {
  acceptedTypes: ['image/*', 'video/*', 'audio/*'],
  maxSize: 100 * 1024 * 1024, // 100MB
  multiple: true,
  dropZone: true,
};

export const GENERIC_UPLOAD_CONFIG: FileUploadConfig = {
  acceptedTypes: [],
  maxSize: 100 * 1024 * 1024, // 100MB
  multiple: true,
  dropZone: true,
};

// Helper functions for easier configuration
export function createFileUploadConfig(
  acceptedTypes: string[],
  maxSizeMB: number,
  multiple = false,
  dropZone = true
): FileUploadConfig {
  return {
    acceptedTypes,
    maxSize: maxSizeMB * 1024 * 1024,
    multiple,
    dropZone,
  };
}

// Common file type groups
export const FILE_TYPES = {
  IMAGES: ['image/*'],
  DOCUMENTS: ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.odt'],
  SPREADSHEETS: ['.csv', '.xlsx', '.xls', '.ods'],
  PRESENTATIONS: ['.ppt', '.pptx', '.odp'],
  ARCHIVES: ['.zip', '.rar', '.7z', '.tar', '.gz'],
  VIDEOS: ['video/*'],
  AUDIO: ['audio/*'],
  CODE: ['.js', '.ts', '.jsx', '.tsx', '.py', '.java', '.cpp', '.c', '.php', '.rb', '.go'],
  DATA: ['.json', '.xml', '.yaml', '.yml', '.sql', '.csv'],
} as const;

export interface FileUploadDialogData {
  title?: string;
  description?: string;
  config?: FileUploadConfig;
  selectButtonText?: string;
  dragMessage?: string;
  uploadButtonText?: string;
  cancelButtonText?: string;
  showMaxFileSize?: boolean;
  icon: string;
}

export interface FileUploadDialogResult {
  files: File[];
  success: boolean;
}

export abstract class FileUploadDialog {
  abstract open(data: FileUploadDialogData, uploaderFn: (files: File[]) => any): any;
}
